import React, { useState, useEffect, useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity, FlatList } from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { Accordion } from '@/components';
import { FeatureFlagsSkeleton } from '../FeatureFlagsSkeleton';
import { useLazyListAllFeatureFlagsQuery } from '@/store/api/admin/featureFlagsApi';
import { FeatureFlagAttributes } from '@/types/admin/featureFlags';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { DesktopFeatureFlagDetailsPanel } from './DesktopFeatureFlagDetailsPanel';
import { useFeatureFlagsData } from '../hooks/useFeatureFlagsData';
import { useOrganizationFlagsEditor } from '../hooks/useOrganizationFlagsEditor';
import { useOrgDotColor } from '../utils/featureFlagsUtils';
import { PropertyFlagRow } from '../components/PropertyFlagRow';

export const DesktopFeatureFlagsView: React.FC = () => {
  const { redesign, admin } = useAdminColors();
  const getOrgDotColor = useOrgDotColor();
  const [getFeatureFlags, { data, isLoading }] = useLazyListAllFeatureFlagsQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFlag, setSelectedFlagLocal] = useState<FeatureFlagAttributes | null>(null);
  const [isOrganizationView, setIsOrganizationView] = useState(false);
  const [forceOpenIncrement, setForceOpenIncrement] = useState(0);

  const { organizationSections } = useFeatureFlagsData({ data, searchTerm });

  const handleSelectRow = (flag: FeatureFlagAttributes, isOrgView: boolean) => {
    setSelectedFlagLocal(flag);
    setIsOrganizationView(isOrgView);
  };

  const { handleEditOrganization } = useOrganizationFlagsEditor(handleSelectRow);

  useEffect(() => {
    getFeatureFlags();
  }, [getFeatureFlags]);

  const hasActiveSearch = useMemo(() => !!searchTerm.trim(), [searchTerm]);

  useEffect(() => {
    if (hasActiveSearch) {
      setForceOpenIncrement((prev) => prev + 1);
    } else {
      setForceOpenIncrement(0);
    }
  }, [hasActiveSearch]);

  const renderContent = () => {
    if (isLoading) {
      return <FeatureFlagsSkeleton />;
    }

    if (!data?.response || data.response.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={[styles.emptyTitle, { color: redesign.text.secondary }]}>
            No Feature Flags
          </Text>
          <Text style={[styles.emptySubtitle, { color: redesign.text.secondary }]}>
            No feature flags have been created yet.
          </Text>
        </View>
      );
    }

    return (
      <FlatList
        style={styles.list}
        data={organizationSections}
        keyExtractor={(item) => item.orgName}
        renderItem={({ item }) => {
          const { orgName, flags } = item;
          const count = flags.length;
          const forceOpen = count > 0 ? forceOpenIncrement : undefined;

          return (
            <Accordion
              forceOpen={forceOpen}
              openWhen={hasActiveSearch && count > 0}
              closeWhen={!hasActiveSearch || (hasActiveSearch && count === 0)}
              header={(
                <View style={styles.orgHeaderLeft}>
                  <View style={[styles.orgDot, { backgroundColor: getOrgDotColor(orgName) }]} />
                  <Text style={[styles.orgName, { color: redesign.text.primary }]}>
                    {orgName}
                  </Text>
                  <Text style={[styles.orgCount, { color: redesign.text.secondary }]}>
                    {count}
                  </Text>
                </View>
              )}
              headerRightComponent={(
                <TouchableOpacity
                  style={styles.editOrgButton}
                  onPress={() => handleEditOrganization(flags)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  activeOpacity={0.6}
                >
                  <SVG_COMPONENTS.PENCIL
                    width={16}
                    height={16}
                    color={redesign.text.primary}
                  />
                </TouchableOpacity>
              )}
            >
              {flags.map((flag) => (
                <PropertyFlagRow
                  key={`${flag.organization_id}-${flag.property_id || flag.id}`}
                  flag={flag}
                  onPress={() => handleSelectRow(flag, false)}
                />
              ))}
            </Accordion>
          );
        }}
        showsVerticalScrollIndicator={false}
      />
    );
  };

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Feature Flags"
        showAddButton={false}
      >
        <View
          style={[
            styles.searchContainer,
            {
              backgroundColor: admin.formInput.bg,
              borderColor: admin.formInput.border,
            },
          ]}
        >
          <SVG_COMPONENTS.MAGNIFYING_GLASS
            width={16}
            height={16}
            color={redesign.text.subtle}
            style={styles.searchIcon}
          />
          <TextInput
            style={[styles.searchInput, { color: redesign.text.primary }]}
            placeholder="Search flags..."
            value={searchTerm}
            onChangeText={setSearchTerm}
            placeholderTextColor={redesign.text.subtle}
          />
        </View>
      </DesktopPageHeader>

      <View style={styles.content}>{renderContent()}</View>

      <DesktopFeatureFlagDetailsPanel
        selectedFlag={selectedFlag}
        isOrganizationView={isOrganizationView}
        onClose={() => setSelectedFlagLocal(null)}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: 24,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
    height: 40,
    minWidth: 280,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    outlineStyle: 'none',
  } as any,
  list: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '500',
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
  },
  orgHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  orgDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  orgName: {
    fontSize: 18,
    fontWeight: '600',
    marginRight: 12,
  },
  orgCount: {
    fontSize: 14,
  },
  editOrgButton: {
    padding: 8,
    borderRadius: 4,
  },
});
